import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as THREE from "three";
import { Button, Dropdown, Menu, Spin, message } from "antd";
import {
  CloseOutlined,
  DownOutlined,
  EnvironmentOutlined,
  EyeFilled,
  EyeInvisibleFilled,
} from "@ant-design/icons";
import { FaRegMap } from "react-icons/fa";
import {
  DEV_MOD,
  GOOGLE_MAPS_KEY,
  GOOGLE_MAPS_MAP_ID,
  GOOGLE_SOLAR_API_KEY,
  LAT,
  LNG,
  MAPBOX_ACCESS_TOKEN,
  MAPBOX_STYLE,
  MAP_TILE_SCALE,
  MAP_TILE_SIZE,
  MAP_TILE_ZOOM,
  buildMapboxBBoxURL,
  buildMapTileURL,
} from "../helpers/CONSTANT";
import { loadGoogleSolarRgbTileData } from "../helpers/googleSolarUtils";
import { VARS } from "../ThreeJs/Global/variables";
import { sceneManager } from "../ThreeJs/SetUps/SceneManager";

const MAP_PROVIDER_GOOGLE = "google-static";
const MAP_PROVIDER_MAPBOX = "mapbox-static";
const MAP_PROVIDER_SOLAR_RGB = "google-solar-rgb";
const STREET_VIEW_TAB = "street-view";
const MAP_TAB = "map";
const STREET_VIEW_DEFAULT_WIDTH_VW = 0.46;
const STREET_VIEW_DEFAULT_WIDTH_MAX = 760;
const STREET_VIEW_DEFAULT_WIDTH_MIN = 320;
const STREET_VIEW_MIN_WIDTH_RATIO = 0.3;
const STREET_VIEW_TRANSITION_MS = 220;

const getGoogleMapsLoaderPromise = () => {
  if (window.google?.maps?.StreetViewPanorama && window.google?.maps?.importLibrary) {
    return Promise.resolve(window.google);
  }
  if (window.__eeGoogleMapsLoadPromise) return window.__eeGoogleMapsLoadPromise;

  window.__eeGoogleMapsLoadPromise = new Promise((resolve, reject) => {
    if (!GOOGLE_MAPS_KEY) {
      reject(new Error("Missing Google Maps key for Street View."));
      return;
    }
    const callbackName = `__eeGoogleMapsInit_${Date.now()}_${Math.floor(Math.random() * 100000)}`;
    window[callbackName] = () => {
      delete window[callbackName];
      resolve(window.google);
    };
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GOOGLE_MAPS_KEY)}&libraries=geometry&loading=async&v=weekly&callback=${callbackName}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      delete window[callbackName];
      reject(new Error("Failed to load Google Maps script."));
    };
    document.head.appendChild(script);
  });
  return window.__eeGoogleMapsLoadPromise;
};

const findNearestPanoramaData = async (googleMaps, lat, lng) => {
  const service = new googleMaps.StreetViewService();
  for (const { radius, source } of [
    { radius: 80, source: googleMaps.StreetViewSource?.OUTDOOR },
    { radius: 200, source: googleMaps.StreetViewSource?.OUTDOOR },
    { radius: 400, source: googleMaps.StreetViewSource?.DEFAULT },
    { radius: 600, source: undefined },
  ]) {
    // eslint-disable-next-line no-await-in-loop
    const data = await new Promise((resolve) => {
      const request = { location: { lat, lng }, radius };
      if (googleMaps.StreetViewPreference?.NEAREST != null) request.preference = googleMaps.StreetViewPreference.NEAREST;
      if (source != null) request.source = source;
      service.getPanorama(request, (result, status) => {
        resolve(status === googleMaps.StreetViewStatus.OK && result?.location?.pano ? result : null);
      });
    });
    if (data) return data;
  }
  return null;
};

const getInitialPovTowardSite = (googleMaps, panoramaData, latitude, longitude) => {
  const pov = { heading: 0, pitch: 0, zoom: 0 };
  if (!googleMaps.geometry?.spherical?.computeHeading || !googleMaps.LatLng || !panoramaData?.location?.latLng) return pov;
  try {
    pov.heading = googleMaps.geometry.spherical.computeHeading(panoramaData.location.latLng, new googleMaps.LatLng(latitude, longitude));
  } catch {
    // Keep the usable default heading.
  }
  return pov;
};

const getStreetViewDefaultWidth = () => {
  if (typeof window === "undefined") return STREET_VIEW_DEFAULT_WIDTH_MIN;
  return Math.max(STREET_VIEW_DEFAULT_WIDTH_MIN, Math.min(STREET_VIEW_DEFAULT_WIDTH_MAX, Math.round(window.innerWidth * STREET_VIEW_DEFAULT_WIDTH_VW)));
};

const clampStreetViewWidth = (width, baseWidth) => {
  const safeBase = Math.max(STREET_VIEW_DEFAULT_WIDTH_MIN, Number(baseWidth) || 0);
  const minWidth = Math.max(180, Math.round(safeBase * STREET_VIEW_MIN_WIDTH_RATIO));
  const canvasWidth = document.querySelector(".designer-canvas")?.clientWidth || window.innerWidth;
  return Math.max(minWidth, Math.min(Math.max(minWidth, Math.floor(canvasWidth * 0.5)), Number(width) || safeBase));
};

const getProviderLabel = (provider) => ({
  [MAP_PROVIDER_GOOGLE]: "Google Maps",
  [MAP_PROVIDER_MAPBOX]: "Mapbox",
  [MAP_PROVIDER_SOLAR_RGB]: "Google Maps HD",
}[provider] || "Google Maps HD");

const buildMapboxUrl = (latitude, longitude) => {
  if (!MAPBOX_ACCESS_TOKEN) return "";
  const size = Math.min(1280, Math.max(64, MAP_TILE_SIZE));
  return `https://api.mapbox.com/styles/v1/${MAPBOX_STYLE}/static/${longitude},${latitude},${MAP_TILE_ZOOM},0/${size}x${size}@2x?access_token=${encodeURIComponent(MAPBOX_ACCESS_TOKEN)}`;
};

const buildLidarAlignedMapboxUrl = (latitude, longitude) => {
  const lidarMesh = sceneManager.scene?.getObjectByName?.("lidarTerrainMesh");
  // Same fallback as the sister app: Mapbox remains available before the
  // optional LiDAR terrain has been created.
  if (!lidarMesh) return buildMapboxUrl(latitude, longitude);
  const bounds = new THREE.Box3().setFromObject(lidarMesh);
  const bboxUrl = buildMapboxBBoxURL({
    centerLat: latitude,
    centerLng: longitude,
    lidarMinX: bounds.min.x,
    lidarMaxX: bounds.max.x,
    lidarMinY: bounds.min.y,
    lidarMaxY: bounds.max.y,
    size: MAP_TILE_SIZE,
    retina: MAP_TILE_SCALE > 1,
  });
  return bboxUrl || buildMapboxUrl(latitude, longitude);
};

const MapTileButton = ({ currentDesign }) => {
  const [showMapTile, setShowMapTile] = useState(true);
  // The scene initially renders the regular Google static tile. Only mark HD
  // as selected after its Solar texture has actually loaded successfully.
  const [mapTileProvider, setMapTileProvider] = useState(
    () => VARS.mapTile?.userData?.mapTileProvider || MAP_PROVIDER_GOOGLE,
  );
  const [loadingMapTile, setLoadingMapTile] = useState(false);
  const [showStreetView, setShowStreetView] = useState(false);
  const [renderStreetView, setRenderStreetView] = useState(false);
  const [loadingStreetView, setLoadingStreetView] = useState(false);
  const [loadingStreetViewFrame, setLoadingStreetViewFrame] = useState(false);
  const [activePreviewTab, setActivePreviewTab] = useState(STREET_VIEW_TAB);
  const [loadingMapPreview, setLoadingMapPreview] = useState(false);
  const [streetViewBaseWidth, setStreetViewBaseWidth] = useState(getStreetViewDefaultWidth);
  const [streetViewWidth, setStreetViewWidth] = useState(getStreetViewDefaultWidth);
  const [streetViewEntered, setStreetViewEntered] = useState(false);
  const streetViewContainerRef = useRef(null);
  const mapPreviewContainerRef = useRef(null);
  const panoramaRef = useRef(null);
  const mapPreviewRef = useRef(null);
  const closeTimerRef = useRef(null);
  const resizeCleanupRef = useRef(() => {});
  // The Mapbox request uses the LiDAR footprint once it exists. Keep that
  // texture off-screen until the user explicitly selects Mapbox.
  const mapboxPreloadRef = useRef({ url: "", texture: null, promise: null });

  const latitude = Number(DEV_MOD ? LAT : currentDesign?.latitude ?? VARS.Current_Project?.latitude);
  const longitude = Number(DEV_MOD ? LNG : currentDesign?.longitude ?? VARS.Current_Project?.longitude);
  const solarDataLayers =
    currentDesign?.googleSolarRaw?.dataLayers ||
    currentDesign?.googleSolar?.dataLayers ||
    window.__GOOGLE_SOLAR_RAW__?.dataLayers ||
    window.__GOOGLE_SOLAR_SUMMARY__?.dataLayers ||
    sceneManager.scene?.getObjectByName?.("lidarTerrainMesh")?.userData?.lidarMeta?.googleSolar?.dataLayers ||
    null;

  const loadMapboxTexture = useCallback((url) => new Promise((resolve, reject) => {
    new THREE.TextureLoader().load(url, resolve, undefined, reject);
  }), []);

  const preloadMapboxTexture = useCallback(() => {
    const url = buildLidarAlignedMapboxUrl(latitude, longitude);
    if (!url) return Promise.resolve(null);

    const cache = mapboxPreloadRef.current;
    if (cache.url === url) return cache.texture ? Promise.resolve(cache.texture) : cache.promise;

    // A LiDAR rebuild can change the clipped footprint. Never retain the old
    // off-screen texture, and discard a late response for its old request.
    cache.texture?.dispose?.();
    cache.url = url;
    cache.texture = null;
    cache.promise = loadMapboxTexture(url)
      .then((texture) => {
        if (mapboxPreloadRef.current.url !== url) {
          texture.dispose?.();
          return null;
        }
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.needsUpdate = true;
        mapboxPreloadRef.current.texture = texture;
        return texture;
      })
      .catch((error) => {
        if (mapboxPreloadRef.current.url === url) {
          mapboxPreloadRef.current.promise = null;
        }
        throw error;
      });
    return cache.promise;
  }, [latitude, loadMapboxTexture, longitude]);

  const takeMapboxTexture = useCallback(async () => {
    const url = buildLidarAlignedMapboxUrl(latitude, longitude);
    if (!url) throw new Error("Unable to build Mapbox map tile URL.");

    let cache = mapboxPreloadRef.current;
    if (cache.url !== url || (!cache.texture && !cache.promise)) {
      await preloadMapboxTexture();
      cache = mapboxPreloadRef.current;
    }
    const texture = cache.url === url
      ? (cache.texture || await cache.promise)
      : null;
    if (!texture) throw new Error("Mapbox map tile preload was superseded.");

    // The material now owns this texture. Remove it from the background cache
    // so later invalidation cannot dispose the actively displayed map.
    if (cache.url === url && cache.texture === texture) {
      cache.url = "";
      cache.texture = null;
      cache.promise = null;
    }
    return texture;
  }, [latitude, longitude, preloadMapboxTexture]);

  const setMapTileTexture = useCallback(async (provider) => {
    const mapTile = VARS.mapTile;
    if (!mapTile) throw new Error("Map tile is not ready yet.");
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) throw new Error("Missing latitude/longitude for map tile.");

    let texture;
    if (provider === MAP_PROVIDER_SOLAR_RGB) {
      const availableSolarDataLayers =
        solarDataLayers ||
        window.__GOOGLE_SOLAR_RAW__?.dataLayers ||
        window.__GOOGLE_SOLAR_SUMMARY__?.dataLayers ||
        sceneManager.scene?.getObjectByName?.("lidarTerrainMesh")?.userData?.lidarMeta?.googleSolar?.dataLayers ||
        null;
      if (!availableSolarDataLayers?.rgbUrl || !GOOGLE_SOLAR_API_KEY) {
        throw new Error("Google Maps HD imagery is not available for this project.");
      }
      const tile = await loadGoogleSolarRgbTileData(availableSolarDataLayers, { apiKey: GOOGLE_SOLAR_API_KEY });
      texture = new THREE.DataTexture(tile.rgba, tile.width, tile.height, THREE.RGBAFormat, THREE.UnsignedByteType);
      texture.flipY = true;
    } else if (provider === MAP_PROVIDER_MAPBOX) {
      texture = await takeMapboxTexture();
    } else {
      const url = buildMapTileURL({ centerLat: latitude, centerLng: longitude, zoom: MAP_TILE_ZOOM, size: MAP_TILE_SIZE, scale: MAP_TILE_SCALE });
      if (!url) throw new Error(`Unable to build ${getProviderLabel(provider)} map tile URL.`);
      texture = await new Promise((resolve, reject) => new THREE.TextureLoader().load(url, resolve, undefined, reject));
    }

    texture.colorSpace = THREE.SRGBColorSpace;
    texture.needsUpdate = true;
    const previous = mapTile.material?.map;
    mapTile.material.map = texture;
    mapTile.material.needsUpdate = true;
    mapTile.userData.mapTileProvider = provider;
    previous?.dispose?.();
    setMapTileProvider(provider);
    setShowMapTile(true);
  }, [latitude, longitude, solarDataLayers, takeMapboxTexture]);

  useEffect(() => {
    const scene = sceneManager?.scene;
    const mapTile = VARS.mapTile;
    if (!scene || !mapTile) return;
    mapTile.visible = showMapTile;
  }, [showMapTile]);

  // Once Solar imagery is delivered with the design, apply the sister app's HD default.
  useEffect(() => {
    if (!solarDataLayers?.rgbUrl || !VARS.mapTile) return;
    setMapTileTexture(MAP_PROVIDER_SOLAR_RGB).catch(() => {
      // The existing map remains visible until Solar imagery can be loaded.
    });
  }, [solarDataLayers, setMapTileTexture]);

  useEffect(() => {
    const applySolarDefault = () => {
      setMapTileTexture(MAP_PROVIDER_SOLAR_RGB).catch(() => {
        // Solar data may be unavailable for some sites; retain the existing tile.
      });
    };
    window.addEventListener("lidarMeshReady", applySolarDefault);
    window.addEventListener("GOOGLE_SOLAR_DATA_READY", applySolarDefault);
    return () => {
      window.removeEventListener("lidarMeshReady", applySolarDefault);
      window.removeEventListener("GOOGLE_SOLAR_DATA_READY", applySolarDefault);
    };
  }, [setMapTileTexture]);

  useEffect(() => {
    // Start with the normal centered Mapbox image, then replace it with the
    // LiDAR-clipped version as soon as the terrain has been generated. Neither
    // preload changes the currently selected map tile.
    preloadMapboxTexture().catch(() => {});
    const refreshMapboxPreload = () => { preloadMapboxTexture().catch(() => {}); };
    window.addEventListener("lidarMeshReady", refreshMapboxPreload);
    return () => {
      window.removeEventListener("lidarMeshReady", refreshMapboxPreload);
      const cache = mapboxPreloadRef.current;
      cache.url = "";
      cache.texture?.dispose?.();
      cache.texture = null;
      cache.promise = null;
    };
  }, [preloadMapboxTexture]);

  useEffect(() => {
    if (!showStreetView || !Number.isFinite(latitude) || !Number.isFinite(longitude)) return undefined;
    let cancelled = false;
    const openStreetView = async () => {
      try {
        await getGoogleMapsLoaderPromise();
        if (cancelled) return;
        const googleMaps = window.google?.maps;
        if (!streetViewContainerRef.current || !googleMaps?.StreetViewPanorama) throw new Error("Google Street View is unavailable.");
        const panoramaData = await findNearestPanoramaData(googleMaps, latitude, longitude);
        if (!panoramaData?.location?.pano) throw new Error("No Street View imagery is available near this project.");
        const pov = getInitialPovTowardSite(googleMaps, panoramaData, latitude, longitude);
        panoramaRef.current?.setVisible?.(false);
        panoramaRef.current = new googleMaps.StreetViewPanorama(streetViewContainerRef.current, { pano: panoramaData.location.pano, pov: { heading: pov.heading, pitch: pov.pitch }, zoom: pov.zoom, addressControl: false, fullscreenControl: false, motionTracking: false, enableCloseButton: false, showRoadLabels: true });
        if (!cancelled) setLoadingStreetViewFrame(false);
      } catch (error) {
        if (!cancelled) {
          message.error(error?.message || "Unable to load Google Street View for this location.");
          setShowStreetView(false);
          setRenderStreetView(false);
          setLoadingStreetViewFrame(false);
        }
      } finally {
        if (!cancelled) setLoadingStreetView(false);
      }
    };
    openStreetView();
    return () => { cancelled = true; };
  }, [showStreetView, latitude, longitude]);

  useEffect(() => {
    if (!showStreetView || activePreviewTab !== MAP_TAB) return undefined;
    let cancelled = false;
    const openMapPreview = async () => {
      try {
        setLoadingMapPreview(true);
        const googleMaps = (await getGoogleMapsLoaderPromise()).maps;
        const container = mapPreviewContainerRef.current;
        if (!container || !googleMaps?.importLibrary) throw new Error("Google Maps preview is unavailable.");
        container.replaceChildren();
        try {
          const { Map3DElement } = await googleMaps.importLibrary("maps3d");
          if (cancelled || typeof Map3DElement !== "function") return;
          const map3d = new Map3DElement({ center: { lat: latitude, lng: longitude, altitude: 140 }, range: 180, tilt: 67.5, heading: 20, mode: "HYBRID" });
          map3d.style.width = "100%";
          map3d.style.height = "100%";
          container.append(map3d);
          mapPreviewRef.current = map3d;
        } catch (error) {
          console.warn("3D Maps unavailable; using Google vector map.", error);
          if (!cancelled) mapPreviewRef.current = new googleMaps.Map(container, { center: { lat: latitude, lng: longitude }, zoom: 20, mapTypeId: "hybrid", mapId: GOOGLE_MAPS_MAP_ID || undefined, renderingType: googleMaps.RenderingType?.VECTOR, streetViewControl: false, mapTypeControl: false, fullscreenControl: false });
        }
      } catch (error) {
        if (!cancelled) {
          message.error(error?.message || "Unable to load Google Maps preview.");
          setActivePreviewTab(STREET_VIEW_TAB);
        }
      } finally {
        if (!cancelled) setLoadingMapPreview(false);
      }
    };
    openMapPreview();
    return () => { cancelled = true; };
  }, [activePreviewTab, latitude, longitude, showStreetView]);

  useEffect(() => {
    if (!renderStreetView) return undefined;
    const baseWidth = getStreetViewDefaultWidth();
    setStreetViewBaseWidth(baseWidth);
    setStreetViewWidth(clampStreetViewWidth(baseWidth, baseWidth));
    const frame = window.requestAnimationFrame(() => setStreetViewEntered(true));
    const handleResize = () => setStreetViewWidth((width) => clampStreetViewWidth(width, getStreetViewDefaultWidth()));
    window.addEventListener("resize", handleResize);
    return () => { window.cancelAnimationFrame(frame); window.removeEventListener("resize", handleResize); };
  }, [renderStreetView]);

  useEffect(() => () => { if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current); resizeCleanupRef.current(); panoramaRef.current?.setVisible?.(false); }, []);

  const handleStreetViewOpen = () => {
    if (loadingStreetView || showStreetView) return;
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return message.error("Missing latitude/longitude for Street View.");
    if (!GOOGLE_MAPS_KEY) return message.error("Missing Google Maps key for Street View.");
    setActivePreviewTab(STREET_VIEW_TAB);
    setRenderStreetView(true);
    setShowStreetView(true);
    setLoadingStreetView(true);
    setLoadingStreetViewFrame(true);
  };
  const handleStreetViewClose = () => {
    setStreetViewEntered(false);
    panoramaRef.current?.setVisible?.(false);
    closeTimerRef.current = window.setTimeout(() => { setShowStreetView(false); setRenderStreetView(false); }, STREET_VIEW_TRANSITION_MS);
  };
  const handleResizeStart = useCallback((event) => {
    event.preventDefault();
    const startX = "touches" in event ? event.touches?.[0]?.clientX ?? 0 : event.clientX;
    const startWidth = streetViewWidth;
    const onMove = (moveEvent) => setStreetViewWidth(clampStreetViewWidth(startWidth + (("touches" in moveEvent ? moveEvent.touches?.[0]?.clientX : moveEvent.clientX) - startX), streetViewBaseWidth));
    const onEnd = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onEnd); window.removeEventListener("touchmove", onMove); window.removeEventListener("touchend", onEnd); };
    resizeCleanupRef.current = onEnd;
    window.addEventListener("mousemove", onMove); window.addEventListener("mouseup", onEnd); window.addEventListener("touchmove", onMove, { passive: false }); window.addEventListener("touchend", onEnd);
  }, [streetViewBaseWidth, streetViewWidth]);

  const handleMapTileMenuClick = useCallback(async ({ key }) => {
    if (key === "map-tile-visibility") return setShowMapTile((visible) => !visible);
    if (![MAP_PROVIDER_GOOGLE, MAP_PROVIDER_MAPBOX, MAP_PROVIDER_SOLAR_RGB].includes(key) || loadingMapTile) return;
    try { setLoadingMapTile(true); await setMapTileTexture(key); }
    catch (error) { message.error(error?.message || `Failed to switch map tile to ${getProviderLabel(key)}.`); }
    finally { setLoadingMapTile(false); }
  }, [loadingMapTile, setMapTileTexture]);
  const mapTileMenu = useMemo(() => <Menu onClick={handleMapTileMenuClick} items={[
    MAP_PROVIDER_GOOGLE, MAP_PROVIDER_MAPBOX, MAP_PROVIDER_SOLAR_RGB,
  ].map((provider) => ({ key: provider, label: mapTileProvider === provider ? `${getProviderLabel(provider)} (Selected)` : getProviderLabel(provider) })).concat([{ type: "divider" }, { key: "map-tile-visibility", label: showMapTile ? "Hide Map Tile" : "Show Map Tile" }])} />, [handleMapTileMenuClick, mapTileProvider, showMapTile]);

  const streetViewDrawer = renderStreetView && typeof document !== "undefined" ? createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, pointerEvents: "none" }}><div style={{ position: "absolute", top: "calc(var(--app-header-height-scaled))", bottom: 0, left: 0, width: `${streetViewWidth}px`, minWidth: `${Math.max(180, Math.round(streetViewBaseWidth * STREET_VIEW_MIN_WIDTH_RATIO))}px`, maxWidth: "calc(100vw - 8px)", padding: 12, overflow: "hidden", pointerEvents: "auto", background: "radial-gradient(145% 120% at 0% 100%, rgba(133,145,55,0.24) 0%, rgba(8,34,47,0.985) 56%, rgba(5,20,33,0.995) 100%)", boxShadow: "0 22px 46px rgba(0,0,0,0.55)", transform: streetViewEntered ? "translateX(0)" : "translateX(-100%)", transition: `transform ${STREET_VIEW_TRANSITION_MS}ms ease` }}>
      <div role="separator" aria-orientation="vertical" aria-label="Resize Street View" onMouseDown={handleResizeStart} onTouchStart={handleResizeStart} style={{ position: "absolute", top: 12, right: -6, width: 12, height: "calc(100% - 24px)", zIndex: 5, cursor: "col-resize", touchAction: "none" }} />
      <div style={{ position: "relative", width: "100%", height: "100%", borderRadius: 12, overflow: "hidden", background: "#0b1e26" }}>
        <div style={{ position: "absolute", top: 10, left: 10, zIndex: 4, display: "flex", gap: 4, padding: 4, borderRadius: 999, background: "rgba(4,13,20,.72)" }}>{[{ key: STREET_VIEW_TAB, label: "Street View" }, { key: MAP_TAB, label: "Map" }].map(({ key, label }) => <button key={key} type="button" onClick={() => setActivePreviewTab(key)} style={{ border: "none", borderRadius: 999, padding: "7px 14px", cursor: "pointer", background: activePreviewTab === key ? "#E5E046" : "transparent", color: activePreviewTab === key ? "#0b1e26" : "#fff" }}>{label}</button>)}</div>
        <button type="button" onClick={handleStreetViewClose} aria-label="Close Street View" style={{ position: "absolute", top: 10, right: 10, zIndex: 4, width: 26, height: 26, borderRadius: "50%", border: "1px solid rgba(255,255,255,.4)", background: "rgba(0,0,0,.5)", color: "#fff", cursor: "pointer" }}><CloseOutlined /></button>
        {((activePreviewTab === STREET_VIEW_TAB && loadingStreetViewFrame) || (activePreviewTab === MAP_TAB && loadingMapPreview)) ? <div style={{ position: "absolute", inset: 0, zIndex: 3, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(11,30,38,.55)" }}><Spin tip={activePreviewTab === MAP_TAB ? "Loading 3D Map..." : "Loading Street View..."} /></div> : null}
        <div ref={streetViewContainerRef} style={{ position: "absolute", inset: 0, background: "#101f26", opacity: activePreviewTab === STREET_VIEW_TAB ? 1 : 0, pointerEvents: activePreviewTab === STREET_VIEW_TAB ? "auto" : "none" }} />
        <div ref={mapPreviewContainerRef} style={{ position: "absolute", inset: 0, background: "#101f26", opacity: activePreviewTab === MAP_TAB ? 1 : 0, pointerEvents: activePreviewTab === MAP_TAB ? "auto" : "none" }} />
      </div>
    </div></div>, document.body) : null;

  const textStrong = "var(--ui-text-strong)";
  const textMuted = "var(--ui-text-muted)";
  return <><div style={{ display: "flex", gap: "10px" }}>
    <Dropdown overlay={mapTileMenu} trigger={["click"]} placement="bottomLeft"><Button type="primary" className="show-tile" loading={loadingMapTile} style={{ padding: "2px", background: showMapTile ? "#1AAA65" : "#fff", color: showMapTile ? textStrong : textMuted, borderRadius: 0, border: "none", display: "flex", alignItems: "center", fontWeight: showMapTile ? "300" : "500" }}><FaRegMap style={{ fontSize: "18px", margin: "0 5px" }} />{`Show Map Tile (${getProviderLabel(mapTileProvider)})`}{showMapTile ? <EyeInvisibleFilled style={{ color: textStrong, fontSize: "18px", margin: "0 5px 0 15px", opacity: ".7" }} /> : <EyeFilled style={{ margin: "0 5px 0 15px", color: textMuted, fontSize: "19px" }} />}<DownOutlined style={{ marginRight: 5, fontSize: 11 }} /></Button></Dropdown>
    <Button type="primary" className="show-tile" loading={loadingStreetView} onClick={handleStreetViewOpen} style={{ padding: "2px 10px", background: showStreetView ? "#1AAA65" : "#fff", color: showStreetView ? textStrong : textMuted, borderRadius: 0, border: "none", display: "flex", alignItems: "center", gap: 4, fontWeight: showStreetView ? "300" : "500" }}><EnvironmentOutlined style={{ fontSize: "16px" }} />Show Street View</Button>
  </div>{streetViewDrawer}</>;
};

export default MapTileButton;
